import { useCallback, useEffect, useState } from "react";
import { AuthInterceptor } from "../../services/AuthInterceptor.Service";
import Spinnerr from "../Commaon/Spinner";
import Toaster from "../Commaon/Toaster";
import { CgEye, CgPen, CgTrash } from "react-icons/cg"; // icons for edit/delete
import DeleteConfirmModal from "../Commaon/DeleteConfirmModal";
import { useNavigate } from "react-router-dom";
import { Modal } from "react-bootstrap";
import ListHeader from "../Commaon/ListHeader";
import ListPagination from "../Commaon/ListPagination";

const PackagesList = () => {
  const navigate = useNavigate();

  const [filteredpackages, setFilteredPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState(null);
  const [deleteModel, setDeleteModel] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteIds, setDeleteIds] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [packageTestModel, setPackageTestModel] = useState(false);
  const [packageTestId, setPackageTestId] = useState("");
  const [packageTestConfirm, setPackageTestConfirm] = useState(false);
 
  // search + pagination states
  const [query, setQuery] = useState("");
  const [pageNo, setPageNo] = useState(1);
  const [limit, setLimit] = useState(25);
  const [totalRecords, setTotalRecords] = useState(0);

  const getPackage = useCallback(async () => {
    setLoading(true);
    try {
      const response = await AuthInterceptor(
        `/admin/package?page=${pageNo}&limit=${limit}&search=${encodeURIComponent(query.trim())}`,
        { method: "GET" }
      );
      const data = response.data;
      if (data.s === 200) {
        setFilteredPackages(data.r || []);
        setTotalRecords(data.tr || 0);
      }
    } catch (err) {
      console.error("Failed to fetch Packages:", err);
      setSnack({
        notification: { message: "Error fetching Packages", type: "error" },
      });
      setFilteredPackages([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, [pageNo, limit, query]);

  useEffect(() => {
    getPackage();
  }, [getPackage]);

  useEffect(() => {
    setPageNo(1);
  }, [query]);

  const startIndex = (pageNo - 1) * limit;
  const totalPages = Math.ceil(totalRecords / limit) || 1;
  const paginatedCategories = filteredpackages;
  const visibleIds = paginatedCategories.map((item) => item._id).filter(Boolean);
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));

  const toggleRowSelection = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((selectedId) => selectedId !== id) : [...prev, id]
    );
  };

  const toggleVisibleSelection = () => {
    setSelectedIds((prev) =>
      allVisibleSelected
        ? prev.filter((id) => !visibleIds.includes(id))
        : Array.from(new Set([...prev, ...visibleIds]))
    );
  };


  const deletePackages = (id) => {
    setDeleteModel(true);
    setDeleteId(id);
    setDeleteIds([]);
  };

  const deleteSelectedPackages = () => {
    if (!selectedIds.length) {
      setSnack({
        notification: { message: "Select at least one package", type: "error" },
      });
      return;
    }
    setDeleteId(null);
    setDeleteIds(selectedIds);
    setDeleteModel(true);
  };

  const runBulkAction = async (action) => {
    if (!selectedIds.length) {
      setSnack({
        notification: { message: "Select at least one package", type: "error" },
      });
      return;
    }

    if (action === "delete") {
      deleteSelectedPackages();
      return;
    }

    const isActive = action === "activate";
    // Skip packages already in the target state
    const ids = selectedIds.filter(
      (id) => filteredpackages.find((item) => item._id === id)?.isActive !== isActive
    );
    try {
      setLoading(true);
      const results = await Promise.allSettled(
        ids.map((id) =>
          AuthInterceptor(`/admin/package/${id}/status`, {
            method: "PATCH",
            body: JSON.stringify({ isActive }),
          })
        )
      );
      const failed = results.filter(
        (r) => r.status !== "fulfilled" || r.value?.data?.s !== 200
      ).length;
      setSnack({
        notification: failed
          ? { message: `Updated ${ids.length - failed} of ${ids.length} packages`, type: "error" }
          : { message: "Bulk action completed", type: "success" },
      });
      setSelectedIds([]);
      getPackage();
    } finally {
      setLoading(false);
    }
  };

  const togglePackageStatus = async (item) => {
    try {
      setLoading(true);
      const response = await AuthInterceptor(`/admin/package/${item._id}/status`, {
        method: "PATCH",
        body: JSON.stringify({ isActive: !item.isActive }),
      });
      if (response?.data?.s === 200 || response?.status === 200) {
        setSnack({
          notification: { message: "Package status updated", type: "success" },
        });
        getPackage();
      }
    } catch (err) {
      setSnack({
        notification: { message: "Error updating package status", type: "error" },
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePackage = async () => {
    const ids = deleteIds.length ? deleteIds : deleteId ? [deleteId] : [];
    if (!ids.length) return;

    try {
      setLoading(true);
      const response = await AuthInterceptor("/admin/package", {
        method: "DELETE",
        body: JSON.stringify({ ids }),
      });
      const data = response.data;

      if (data.s === 200) {
        setSnack({
          notification: {
            message: data.m || "Package deleted successfully",
            type: "success",
          },
        });
        setSelectedIds((prev) => prev.filter((id) => !ids.includes(id)));
        const remainingRecords = Math.max(totalRecords - ids.length, 0);
        const lastPage = Math.ceil(remainingRecords / limit) || 1;
        if (pageNo > lastPage) {
          setPageNo(lastPage);
        } else {
          getPackage();
        }
      } else {
        throw new Error(data.m || "Error deleting package");
      }
    } catch (err) {
      console.error("Error deleting package:", err);
      setSnack({
        notification: {
          message: err.message || "Error deleting package",
          type: "error",
        },
      });
    } finally {
      setLoading(false);
      setDeleteModel(false);
      setDeleteId(null);
      setDeleteIds([]);
    }
  };

  // Validate the entered id, then ask for confirmation before deleting.
  const requestDeletePackageTest = (e) => {
    e.preventDefault();
    if (!packageTestId.trim()) {
      setSnack({
        notification: { message: "Package test id is required", type: "error" },
      });
      return;
    }
    setPackageTestModel(false);
    setPackageTestConfirm(true);
  };

  const handleDeletePackageTest = async () => {
    let deleted = false;
    try {
      setLoading(true);
      const response = await AuthInterceptor(`/admin/package-test/${packageTestId}`, {
        method: "DELETE",
      });
      if (response?.data?.s === 200 || response?.status === 200) {
        setSnack({
          notification: { message: "Package test deleted successfully", type: "success" },
        });
        deleted = true;
        setPackageTestId("");
        getPackage();
      } else {
        setSnack({
          notification: { message: response?.data?.m || "Error deleting package test", type: "error" },
        });
      }
    } catch (err) {
      setSnack({
        notification: { message: "Error deleting package test", type: "error" },
      });
    } finally {
      setLoading(false);
      setPackageTestConfirm(false);
      // On failure, return to the id form (as before) so the id can be corrected.
      if (!deleted) setPackageTestModel(true);
    }
  };

  return (
    <>
      {loading && <Spinnerr show={loading} />}
      {snack && (
        <Toaster
          notification={snack.notification}
          onClose={() => setSnack(null)}
        />
      )}

      <div className="card shadow-sm border-0 rounded-3 p-3">
        <ListHeader
          title="Packages List"
          searchValue={query}
          searchPlaceholder="Search packages..."
          onSearchChange={setQuery}
          limit={limit}
          onLimitChange={(size) => {
            setPageNo(1);
            setLimit(size);
          }}
        />

        {/* Bulk actions | Add */}
        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          {/* Bulk actions only make sense when there are rows to select */}
          {paginatedCategories.length > 0 && (
            <>
              <span className="text-muted small">{selectedIds.length} selected</span>
              <button className="btn btn-sm btn-outline-secondary" onClick={() => runBulkAction("activate")}>
                Active
              </button>
              <button className="btn btn-sm btn-outline-secondary" onClick={() => runBulkAction("inactive")}>
                Inactive
              </button>
              <button className="btn btn-sm btn-outline-danger" onClick={() => runBulkAction("delete")}>
                Delete
              </button>
            </>
          )}
          <button
            className="btn btn-sm btn-outline-custom ms-auto"
            onClick={() => setPackageTestModel(true)}
          >
            Remove Package
          </button>
          <button
            className="btn btn-sm btn-outline-custom"
            onClick={() => navigate("/packages/add")}
          >
            Add Packages
          </button>
        </div>

        {/* Table */}
        <div className="table-responsive">
          <table className="table table-hover align-middle" style={{ tableLayout: "fixed", width: "100%" }}>
            <thead className="table-light">
              <tr>
                <th style={{ width: "48px" }}>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={allVisibleSelected}
                    onChange={toggleVisibleSelection}
                    aria-label="Select all packages on this page"
                  />
                </th>
                <th style={{ width: "60px" }}>Sr.</th>
                <th>Title</th>
                <th>Description</th>
                <th>Original Price</th>
                <th>Sample Type</th>
                <th>Status</th>
                <th style={{ width: "150px" }} className="text-start">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody>
              {!loading &&
                paginatedCategories.length > 0 &&
                paginatedCategories.map((c, idx) => (
                  <tr key={c._id || idx}>
                    <td>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={selectedIds.includes(c._id)}
                        onChange={() => toggleRowSelection(c._id)}
                        aria-label={`Select ${c.title || "package"}`}
                      />
                    </td>
                    <td>{startIndex + idx + 1}</td>
                    <td>{c.title || "-"}</td>
                    <td>{c.description || "-"}</td>
                    <td>{c.originalPrice || "-"}</td>
                    <td>{c.sampleType || "-"}</td>
                    <td>
                      {c.isActive ? (
                        <span className="badge bg-success" role="button" onClick={() => togglePackageStatus(c)}>Active</span>
                      ) : (
                        <span className="badge bg-secondary" role="button" onClick={() => togglePackageStatus(c)}>Inactive</span>
                      )}
                    </td>
                    
                    <td>
                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => navigate(`/packages/view/${c._id}`)}
                        >
                          <CgEye size={16} />
                        </button>

                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => navigate(`/packages/edit/${c._id}`)}
                        >
                          <CgPen size={16} />
                        </button>

                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => deletePackages(c._id)}
                        >
                          <CgTrash size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}

              {!loading && paginatedCategories.length === 0 && (
                <tr>
                  <td colSpan="8" className="text-center text-muted py-4">
                    No packages found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <ListPagination
          show={totalRecords > 0}
          pageNo={pageNo}
          totalPages={totalPages}
          onPageChange={setPageNo}
        />
      </div>

      <Modal show={packageTestModel} onHide={() => setPackageTestModel(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Remove Package Test</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={requestDeletePackageTest}>
            <div className="mb-3">
              <label className="form-label">Package Test ID</label>
              <input
                className="form-control"
                value={packageTestId}
                onChange={(e) => setPackageTestId(e.target.value)}
              />
            </div>
            <div className="d-flex justify-content-end gap-2">
              <button type="button" className="btn btn-outline-secondary" onClick={() => setPackageTestModel(false)}>
                Cancel
              </button>
              <button type="submit" className="btn btn-outline-danger">
                Delete
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>

      {/* Confirm Remove Package Test */}
      <DeleteConfirmModal
        show={packageTestConfirm}
        count={1}
        singular="package test"
        plural="package tests"
        loading={loading}
        onConfirm={handleDeletePackageTest}
        onCancel={() => {
          setPackageTestConfirm(false);
          setPackageTestId("");
        }}
      />

      {/* Delete Package Modal */}
      <DeleteConfirmModal
        show={deleteModel}
        count={deleteIds.length || 1}
        singular="package"
        plural="packages"
        loading={loading}
        onConfirm={() => {
          handleDeletePackage();
          setDeleteModel(false);
        }}
        onCancel={() => {
          setDeleteModel(false);
          setDeleteId(null);
          setDeleteIds([]);
        }}
      />
    </>
  );
};

export default PackagesList;
